import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import { transformResponseHandler } from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const exitWindowApi = createApi({
  reducerPath: REDUCER_PATHS.EXIT_WINDOW,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    //exitWindow
    exitWindowList: builder.query({
      query: (data) => ({
        url: `${apiConfig.EXIT_WINDOW}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["exitWindowList"],
    }),
    //exitWindowSellRequests
    exitWindowSellRequestsList: builder.query({
      query: (data) => ({
        url: `${apiConfig.EXIT_WINDOW_SELL_REQUESTS}?exitId=${data?.exitId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["exitWindowSellRequestsList"],
    }),
    //exitWindowPropertyDetails
    exitWindowPropertyDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.EXIT_WINDOW_PROPERTY_DETAILS}/${data?.exitWindowId}`,
        method: "GET",
      }),
      providesTags: ["exitWindowPropertyDetails"],
    }),
    //exitWindowPurchaseRequests
    exitWindowPurchaseRequestsList: builder.query({
      query: (data) => ({
        url: `${apiConfig.EXIT_WINDOW_PURCHASE_REQUESTS}?exitId=${data?.exitId}&sellRequestId=${data?.sellRequestId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["exitWindowPurchaseRequestsList"],
    }),
    //exitWindowSellerDetails
    exitWindowSellerDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.EXIT_WINDOW_SELL_REQUESTS}/${data?.exitWindowSellId}`,
        method: "GET",
      }),
      providesTags: ["exitWindowSellerDetails"],
    }),
    editExitWindowList: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.EXIT_WINDOW}/${data?.exitWindowId}`,
        body: data?.body,
        method: "PUT",
      }),
      invalidatesTags: ["exitWindowList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    exitWindowListChangeStatus: builder.mutation({
      query: (data) => ({
        url:
          data?.type === "exitWindowSellRequests"
            ? apiConfig.EXIT_WINDOW_SELL_REQUESTS
            : apiConfig.EXIT_WINDOW_PURCHASE_REQUESTS,
        body: {
          ids: data?.ids,
          status: data?.status,
        },
        method: "PATCH",
      }),
      invalidatesTags: ["exitWindowList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    exitWindowListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.EXIT_WINDOW,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["exitWindowList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addExitWindowList: builder.mutation({
      query: (data) => ({
        url: apiConfig.EXIT_WINDOW,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["exitWindowList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    exitWindowDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.EXIT_WINDOW}/${data?.depositId}`,
        method: "GET",
      }),
      providesTags: ["exitWindowList"],
    }),
    exitWindowListDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();
        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);
        return {
          url: `${apiConfig.EXIT_WINDOW_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["exitWindowList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    exitWindowSellRequestsListDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();
        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);
        return {
          url: `${apiConfig.EXIT_WINDOW_SELLER_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["exitWindowList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    exitWindowPurchaseRequestsListDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();
        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);
        return {
          url: `${apiConfig.EXIT_WINDOW_PURCHASE_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["exitWindowList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    exitWindowStakeDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.EXIT_WINDOW_STAKE_DETAILS}/?exitId=${data?.exitWindowId}&sellRequestId=${data?.sellRequestId}&sellUserId=${data?.sellUserId}`,
        method: "GET",
      }),
      providesTags: ["exitWindowList"],
    }),
    addExitWindowPurchaseRequests: builder.mutation({
      query: (data) => ({
        url: apiConfig.EXIT_WINDOW_PURCHASE_REQUESTS_DETAILS,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["exitWindowList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
  }),
});

export const {
  useAddExitWindowListMutation,
  useLazyExitWindowListQuery,
  useExitWindowListChangeStatusMutation,
  useExitWindowListDeleteMutation,
  useLazyExitWindowDetailsQuery,
  useEditExitWindowListMutation,
  useLazyExitWindowSellRequestsListQuery,
  useLazyExitWindowPropertyDetailsQuery,
  useLazyExitWindowPurchaseRequestsListQuery,
  useLazyExitWindowSellerDetailsQuery,
  useExitWindowListDownloadMutation,
  useExitWindowSellRequestsListDownloadMutation,
  useExitWindowPurchaseRequestsListDownloadMutation,
  useAddExitWindowPurchaseRequestsMutation,
  useLazyExitWindowStakeDetailsQuery,
} = exitWindowApi;

export default exitWindowApi;
